import React, { memo, useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Pill } from "@foris/avocado-suite";
import LinkRouter from "@common/components/link-router";
import { Link } from "@models/ISchema";
import { Header } from "@components/index";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { getLinkTypeGroup } from "@dimensions/utils/linkUtils";
import headerCss from "@components/Header/header.module.scss";

interface ILinkHeader {
  setHeight?: any;
  link: Link;
  isCreation?: boolean;
}
const LinkHeader: React.FC<ILinkHeader> = (props: ILinkHeader) => {
  const { setHeight, link, isCreation } = props;
  const { origin, scenario, workspace } = useContextUrl();
  const { t } = useTranslation();
  const { adaptedHomeUrl } = useNavigationUrl();
  if (!link) return null;

  const routeBundle = `/editor/vacancies/${workspace}/${scenario}/${origin}/${link?.bundle?.id}`;

  const program = link?.course?.curriculum?.program;
  const courseCode = link?.course?.code || "";
  const courseName = link?.course?.name || "";

  const typeGroup = useMemo(() => getLinkTypeGroup(link), [link?.groups]);

  const subTitle = (empty: boolean) => {
    if (empty) return undefined;

    const idBundle = link?.bundle?.id;
    const termCode = link?.bundle?.term?.code;
    const routeBundle = `/editor/vacancies/${workspace}/${scenario}/${origin}/${idBundle}`;

    return (
      !!(idBundle || termCode) && (
        <div className={cx(headerCss.infoItem_subTitle, headerCss.infoItem_subTitleWrapper)}>
          <Pill textColor="gray-10" color="gray-70">
            {t("dimension.link.campus-pill")}: {link?.course?.curriculum?.program?.campus?.code}
          </Pill>
          <Pill textColor="gray-10" color="gray-70">
            {t("dimension.link.label-pill")}: {termCode}
          </Pill>
          <Pill textColor="gray-10" color="gray-70">
            {t("dimension.link.header.more-details.vacancies")}: {link?.vacancies ?? "-"}
          </Pill>
          <LinkRouter
            to={routeBundle}
            className={headerCss.link}
            iconRight="external-link"
            size="sm"
          >
            {`${t("dimension.link.header.more-details.bundle")}: ${link?.bundle?.code}`}
          </LinkRouter>
        </div>
      )
    );
  };

  const topTitle = isCreation
    ? courseCode && courseName
      ? `Liga ${courseCode}, ${courseName}`
      : ""
    : link?.code
    ? `${t("dimension.link.top-title")}: ${link?.code}`
    : "";

  const title = isCreation
    ? t("bundle.packages-edition.title-create", {
        name: courseCode,
      })
    : courseCode && courseName
    ? `${courseCode}, ${courseName}`
    : "";

  const detailsCofiguration = [
    { title: t("dimension.link.header.more-details.campus"), content: program?.campus?.code || "" },
    {
      title: t("dimension.link.header.more-details.department"),
      content: program?.department?.code || "",
    },
    {
      title: t("dimension.link.header.more-details.modality"),
      content: program?.modality?.code || "",
    },
    { title: t("dimension.link.header.more-details.shift"), content: program?.shift?.code || "" },
    { title: t("dimension.link.header.more-details.program"), content: program?.code || "" },
    {
      title: t("dimension.link.header.more-details.curriculum"),
      content: link?.course?.curriculum?.code || "",
    },
    {
      title: t("dimension.link.header.more-details.level"),
      content: link?.course?.level?.toString() || "",
    },
    {
      title: t("dimension.link.header.more-details.total-blocks"),
      content: link?.totalBlocks?.toString() || "",
    },
    {
      title: t("dimension.link.header.more-details.credits"),
      content: link?.course?.credits?.toString() || "",
    },
  ];

  if (typeGroup)
    detailsCofiguration.push({
      title: t("dimension.link.header.more-details.group-types"),
      content: typeGroup,
    });

  const details = (isCreation: boolean) => (isCreation ? undefined : detailsCofiguration);

  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        title: t("sidebar-menu.titles.home"),
        path: adaptedHomeUrl(),
      },
      {
        title: isCreation
          ? t("bundle.packages-edition.title-create-short")
          : t("bundle.packages-edition.title"),
      },
    ];

    if (!isCreation) {
      items.splice(1, 0, {
        title: "Bundle",
        path: routeBundle,
      });
    }

    return items;
  }, [isCreation, routeBundle, t]);

  return (
    <Header
      breadcrumbItems={breadcrumbItems}
      title={title}
      topTitle={topTitle}
      subTitle={subTitle(isCreation)}
      moreDetails={details(isCreation)}
      setHeight={setHeight}
    />
  );
};

export default memo(LinkHeader);
